import { Component, type ReactNode } from 'react';
import { logger } from '../lib/logger';

const log = logger.withContext({ component: 'ErrorBoundary' });

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Error Boundary component to catch React rendering errors
 * and display a fallback UI instead of crashing the entire app
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log error for debugging
    log.error('ErrorBoundary caught an error', { componentStack: errorInfo.componentStack || 'unknown' }, error);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-screen p-4 text-center bg-tg-bg">
          <div className="text-5xl mb-4">💥</div>
          <h2 className="text-xl font-bold mb-2 text-tg-text">
            ¡Ups! Algo salió mal
          </h2>
          <p className="text-tg-hint mb-4 max-w-sm">
            Ha ocurrido un error inesperado. Por favor, intenta de nuevo.
          </p>
          {this.state.error && (
            <p className="text-xs text-red-500 mb-4 max-w-sm break-words">
              {this.state.error.message}
            </p>
          )}
          <button
            onClick={this.handleRetry}
            className="px-6 py-3 bg-tg-button text-tg-button-text rounded-xl font-semibold active:scale-95 transition-transform"
          >
            🔄 Reintentar
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

